import moment from 'moment';
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  InputAdornment,
  Box,
  FormControlLabel,
  Checkbox,
  Popover,
} from '@material-ui/core';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { addBreadcrumbAction, popBreadcrumbAction } from '../../../../actions/breadcrumbsAction';
import { addProductToCategoryAction } from '../../../../actions/categoriesAction';
import {
  addOptionsToProductAction,
  clearProductOptionsAction,
  createProductAction,
  disableProductForDateAction,
  disableProductUntilDateAction,
  restockProductAction,
  updateProductAction,
} from '../../../../actions/productsAction';
import { addProductToBundleOptionAction } from '../../../../actions/bundleOptionsAction';
import { startsWith } from '../../../../helpers/Helpers';
import ProductOptions from './ProductOptions';
import MenuItemImageUpload from '../menuItemImageUpload/MenuItemImageUpload';
import ReviewButton from '../ReviewButton/ReviewButton';
import useStyles from './ProductCreateStyles';
import { dialogContexts } from '../../../../constants/constants';
import InfoPopover from '../../../InfoPopover/InfoPopover';

const ProductEditDialog = ({
  clearProductOptionsAction,
  addOptionsToProductAction,
  addProductToCategoryAction,
  addProductToBundleOptionAction,
  createProductAction,
  updateProductAction,
  restaurantId,
  openDialog,
  setOpenDialog,
  dialogContext,
  addBreadcrumbAction,
  popBreadcrumbAction,
  products,
  product,
  category,
  bundleOptionId,
  options,
  menuVersion,
  restockProductAction,
  disableProductForDateAction,
  disableProductUntilDateAction,
}) => {
  const classes = useStyles();

  const closeDialog = () => {
    popBreadcrumbAction();

    setOpenDialog(false);
  };

  const [save, setSave] = useState(false);

  const [posId, setPosId] = useState(product?.posId || null);
  const [posIdError, setPosIdError] = useState('');

  const [name, setName] = useState(product?.productTitle || '');
  const [nameError, setNameError] = useState('');

  const [price, setPrice] = useState(product?.price || 0);

  const [description, setDesc] = useState(product?.productDescription || '');
  const [descError, setDescError] = useState('');

  const [imageLink, setImageLink] = useState(product?.imageLink || null);
  const [image, setImage] = useState(product?._image || {});

  const [disableDate, setDisableDate] = useState(product?.disableDate || null);

  const [importStatus, setImportStatus] = useState(product?.importStatus || null);
  const [reviewError, setReviewError] = useState('');

  const [isDisabled, setIsDisabled] = useState(
    moment(product?.disableDate, 'yyyy-MM-DD').isSame(moment(), 'day') ||
      moment(product?.disableUntilDate, 'yyyy-MM-DD').isAfter(moment(), 'day'),
  );

  const [disableUntilDate, setDisableUntilDate] = useState(product?.disableUntilDate || null);

  const [newItemId, setNewItemId] = useState(`product_${new Date().getTime()}`);

  const [noDiscount, setNoDiscount] = useState(product?.noDiscount || false);
  const [showNoDiscountConfirmation, setShowNoDiscountConfirmation] = useState(false);

  const [free, setFree] = useState(product?.free || false);
  const [showFreeConfirmation, setShowFreeConfirmation] = useState(false);

  // Show breadcrumbs
  useEffect(() => {
    if (openDialog) {
      addBreadcrumbAction(`${dialogContext} menu item`);
    }
  }, [openDialog]);

  // Reset disable fields when reopened
  useEffect(() => {
    if (openDialog) {
      setDisableDate(product?.disableDate || null);
      setDisableUntilDate(product?.disableUntilDate || null);
      setIsDisabled(!!product?.disableDate || !!product?.disableUntilDate || false);
    }
  }, [openDialog]);

  // Set or unset disable date if the box is checked
  useEffect(() => {
    if (!isDisabled || disableUntilDate) {
      setDisableDate(null);
    } else {
      // if disable option is enabled and no disable date given, set it for today only
      setDisableDate(moment().format('yyyy-MM-DD'));
    }
  }, [isDisabled, disableUntilDate]);

  const getUnreviewedOptions = () =>
    product?.optionIds
      ?.map((optionId) => options?.localData?.find((option) => option?.objectId === optionId))
      ?.filter((option) => option?.importStatus === 'needsReview');

  useEffect(() => {
    setReviewError('');
  }, [options]);

  /**
   * Validate and set the option as reviewed
   */
  const setProductReviewed = (newStatus) => {
    if (newStatus === 'reviewed') {
      // Check all choices are valid
      const unreviewedOptions = getUnreviewedOptions();

      if (unreviewedOptions?.length > 0) {
        setReviewError(
          'You cannot set the menu item as reviewed until all of its options have been reviewed.',
        );
        return;
      }
    }

    setImportStatus(newStatus);
    setReviewError('');
  };

  const formatNumber = (num) => (Math.round(num * 100) / 100).toFixed(2);

  const submit = (event) => {
    event.preventDefault();

    let formError = false;

    if (!name) {
      setNameError('Please enter a name.');
      formError = true;
    } else if (nameError) {
      setNameError('');
    }

    if (formError) {
      return;
    }
    if (dialogContext === dialogContexts.CREATE) {
      createProductAction(
        newItemId,
        name,
        description,
        Number(price),
        imageLink,
        image,
        noDiscount,
        disableDate,
        disableUntilDate,
        restaurantId,
        posId,
      );
    } else {
      updateProductAction(
        product.objectId,
        name,
        description,
        Number(price),
        imageLink,
        image,
        noDiscount,
        disableDate,
        disableUntilDate,
        restaurantId,
        posId,
        importStatus,
      );

      if (!startsWith(product.objectId, 'product_')) {
        if (!isDisabled) {
          if (product.disableDate !== null || product.disableUntilDate !== null) {
            restockProductAction(product.objectId);
          }
        } else {
          if (disableDate !== product.disableDate && disableDate !== null) {
            disableProductForDateAction(product.objectId, disableDate);
          }
          if (disableUntilDate !== product.disableUntilDate && disableUntilDate !== null) {
            disableProductUntilDateAction(product.objectId, disableUntilDate);
          }
        }
      }
    }

    // If category was provided, ensure we add this product to the category too
    if (category) {
      addProductToCategoryAction(newItemId, category.objectId);
    }

    // If bundleOptionId was provided, ensure we add this product to the bundleOptionId too
    if (bundleOptionId) {
      addProductToBundleOptionAction(newItemId, bundleOptionId);
    }

    setSave(true);
    closeDialog();
  };

  const objectId = products?.data?.find(
    (apiProduct) => apiProduct?.objectId === product?.objectId,
  )?.objectId;

  return (
    <Dialog
      open={openDialog}
      onClose={closeDialog}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      fullWidth
      className={classes.dialog}
      maxWidth='lg'
      onEnter={() => {
        setNewItemId(`product_${new Date().getTime()}`);

        // Update local storage's options to match this item
        addOptionsToProductAction(product?.optionIds || []);

        // Reset inputs when entering
        setName(product?.productTitle || '');
        setPrice(product?.price || 0);
        setDesc(product?.productDescription || '');
        setImageLink(product?.imageLink || null);
        setImage(product?._image || {});
        setNoDiscount(product?.noDiscount || false);
        setFree(product?.free || false);
        setDisableDate(product?.disableDate || null);
        setDisableUntilDate(product?.disableUntilDate || null);
        setPosId(product?.posId || null);
        setImportStatus(product?.importStatus || null);

        setNameError('');
        setPosIdError('');
        setDescError('');
        setReviewError('');
        setSave(false);
      }}
      onExited={() => {
        clearProductOptionsAction();
      }}
    >
      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={2}>
          <Grid item md={6} xs={12} className={classes.leftColumn}>
            <Box display='flex' justifyContent='space-between'>
              <Box className={classes.modalTitle}>{dialogContext} Menu Item</Box>
              {menuVersion.posEnabled && (
                <ReviewButton importStatus={importStatus} setStatus={setProductReviewed} />
              )}
            </Box>
            {reviewError && <Box style={{ color: 'red', paddingTop: '20px' }}>{reviewError}</Box>}
            <form onSubmit={submit} id='create-menu-item-form' style={{ paddingTop: '40px' }}>
              <Box className={classes.columnTitle}>Item details</Box>
              <TextField
                autoFocus
                id='menu-item-name'
                label='Name'
                placeholder='eg. Mains, Entrees'
                variant='outlined'
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={(e) => setName(e.target.value.trim())}
                InputLabelProps={{ shrink: true }}
                margin='normal'
                required
                error={Boolean(nameError)}
                helperText={nameError}
              />

              <TextField
                id='menu-item-price'
                label='Price'
                placeholder='10.00'
                variant='outlined'
                fullWidth
                value={price}
                type='number'
                onChange={(e) => setPrice(e.target.value)}
                onBlur={(e) => setPrice(formatNumber(e.target.value))}
                InputLabelProps={{ shrink: true }}
                min={0}
                // inputProps={{ min: 0, step: 1 }}
                InputProps={{
                  startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                }}
                margin='normal'
                required
              />

              <TextField
                multiline
                id='menu-item-desc'
                label='Description'
                placeholder="eg. Grandma's flaky pie crust filled with roast chicken, baby carrots, and spring peas."
                variant='outlined'
                fullWidth
                value={description}
                onChange={(e) => setDesc(e.target.value)}
                onBlur={(e) => setDesc(e.target.value.trim())}
                InputLabelProps={{ shrink: true }}
                margin='normal'
                error={Boolean(descError)}
                helperText={descError}
              />

              <MenuItemImageUpload
                itemId={newItemId}
                imageLink={imageLink}
                setImageLink={setImageLink}
                setImage={setImage}
              />

              {menuVersion.posEnabled && (
                <TextField
                  autoFocus
                  id='menu-item-pos-id'
                  label='POS ID'
                  variant='outlined'
                  fullWidth
                  disabled
                  value={posId}
                  onChange={(e) => setPosId(e.target.value)}
                  onBlur={(e) => setPosId(e.target.value.trim())}
                  InputLabelProps={{ shrink: true }}
                  margin='normal'
                  error={Boolean(posIdError)}
                  helperText={posIdError}
                  InputProps={{ style: { backgroundColor: '#EFEFEF' } }}
                />
              )}

              <Dialog open={showNoDiscountConfirmation}>
                <Box style={{ padding: '20px' }}>
                  <Box mb='20px' style={{ padding: '15px', fontSize: '16px' }}>
                    {' '}
                    Are you sure you want to make this item exempt from discounts? (typically only
                    used for special promotions)
                  </Box>
                  <Button
                    color='primary'
                    onClick={() => {
                      setShowNoDiscountConfirmation(false);
                      setNoDiscount(true);
                    }}
                  >
                    Yes
                  </Button>
                  <Button
                    onClick={() => {
                      setShowNoDiscountConfirmation(false);
                    }}
                  >
                    No
                  </Button>
                </Box>
              </Dialog>

              <Dialog open={showFreeConfirmation}>
                <Box style={{ padding: '20px' }}>
                  <Box mb='20px' style={{ padding: '15px', fontSize: '16px' }}>
                    {' '}
                    Are you sure you want to make this item free?
                  </Box>
                  <Button
                    color='primary'
                    onClick={() => {
                      setShowFreeConfirmation(false);
                      setFree(true);
                    }}
                  >
                    Yes
                  </Button>
                  <Button
                    onClick={() => {
                      setShowFreeConfirmation(false);
                    }}
                  >
                    No
                  </Button>
                </Box>
              </Dialog>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={noDiscount}
                    onChange={(event) =>
                      event.target.checked === true
                        ? setShowNoDiscountConfirmation(true)
                        : setNoDiscount(false)
                    }
                    name='noDiscount'
                    color='primary'
                  />
                }
                label='Exempt from discounts'
              />

              {/* <FormControlLabel */}
              {/*  control={ */}
              {/*    <Checkbox */}
              {/*      checked={free} */}
              {/*      onChange={(event) => event.target.checked === true ? setShowFreeConfirmation(true) : setFree(false)} */}
              {/*      name="free" */}
              {/*      color="primary" */}
              {/*    /> */}
              {/*  } */}
              {/*  label="Item is free" */}
              {/* /> */}

              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isDisabled}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setIsDisabled(true);
                        } else {
                          setIsDisabled(false);
                          setDisableUntilDate(null);
                          setDisableDate(null);
                        }
                      }}
                      name='showDisableOption'
                      color='primary'
                    />
                  }
                  label='Disable item'
                />
              </Box>

              {isDisabled && (
                <TextField
                  id='menu-item-disable-until-date'
                  label='Disable until'
                  type='date'
                  value={disableUntilDate}
                  onChange={(e) => setDisableUntilDate(e.target.value)}
                  onBlur={(e) => setDisableUntilDate(e.target.value.trim())}
                  InputLabelProps={{ shrink: true }}
                  variant='outlined'
                  margin='normal'
                />
              )}
            </form>
          </Grid>
          <Grid item md={6} xs={12} className={classes.rightColumn}>
            <Box className={classes.columnTitle}>Options</Box>
            <ProductOptions productId={product?.objectId || newItemId} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ borderTop: '1px solid #DCDCDB', minHeight: '50px' }}>
        <Box
          style={{ minHeight: '60px', margin: '0 20px', width: '100%' }}
          display='flex'
          justifyContent='space-between'
          alignItems='center'
        >
          <Button onClick={closeDialog} color='default'>
            Cancel
          </Button>

          <InfoPopover
            info={objectId}
            style={{ position: 'absolute', left: 0, right: 0, margin: '0 auto' }}
          />

          <Button onClick={submit} color='primary' type='submit' form='create-menu-item-form'>
            Save Menu Item
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  products: state.products,
  options: state.options,
  restaurantId: state.activeRestaurant.data.objectId,
  menuVersion: state.menuVersion,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      clearProductOptionsAction,
      createProductAction,
      updateProductAction,
      addOptionsToProductAction,
      addBreadcrumbAction,
      popBreadcrumbAction,
      addProductToCategoryAction,
      addProductToBundleOptionAction,
      restockProductAction,
      disableProductForDateAction,
      disableProductUntilDateAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(ProductEditDialog);
